import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../Contexts/AuthContext";

/**
 * ModuleGuard - High-fidelity route protection.
 * 
 * Checks if the current user handles the required module path or 
 * belongs to the required industry type.
 */
export default function ModuleGuard({ children, requiredPath, requiredIndustry }) {
    const { user, navigation, loading, isAuthenticated } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div style={{ 
                height: "100vh", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                background: "#f9fafb" 
            }}>
                <div className="loader">Verifying Workspace...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    const effectiveUser = user || (() => {
        try {
            const stored = localStorage.getItem("user");
            return stored ? JSON.parse(stored) : null;
        } catch (e) {
            return null;
        }
    })();

    // Superusers, Admins, & Managers have full access to workspace modules
    if (
        effectiveUser?.is_superuser ||
        effectiveUser?.user_type === 'admin' ||
        effectiveUser?.user_type === 'manager'
    ) {
        return children;
    }

    const orgDetails = effectiveUser?.organization_details;
    const industryType = orgDetails?.industry_type || orgDetails?.industry_name;

    // 1. Industry Check (Global check for Medical, Education, etc.)
    if (requiredIndustry && industryType && industryType !== requiredIndustry) {
        console.warn(`Access Denied: Required industry '${requiredIndustry}', but user is in '${industryType}'`);
        return <Navigate to="/forbidden" replace />;
    }

    // 2. Module/Navigation Check (Granular check for enabled modules)
    if (requiredPath) {
        const normalizePath = (p) => p ? String(p).replace(/\/$/, "").toLowerCase() : "";
        const normRequired = normalizePath(requiredPath);
        const currentPath = normalizePath(location.pathname);
        const navList = (Array.isArray(navigation) && navigation.length > 0)
            ? navigation
            : (() => {
                try {
                    const stored = localStorage.getItem("navigation");
                    return stored ? JSON.parse(stored) : [];
                } catch (e) {
                    return [];
                }
            })();

        const isMeetingsModuleAllowed = normRequired === "/meetings" && (
            effectiveUser?.organization_details?.has_meetings_module ||
            navList.some(m => m?.codename === "meetings" || m?.path === "/meetings")
        );

        const hasAccess = isMeetingsModuleAllowed || navList.some(module => {
            if (normRequired === "/meetings" && (module?.codename === "meetings" || module?.path === "/meetings")) {
                return true;
            }
            const normModulePath = normalizePath(module?.path);
            if (!normModulePath) return false;
            return normModulePath === normRequired || 
                   (normModulePath !== "" && (normRequired.startsWith(normModulePath) || currentPath.startsWith(normModulePath)));
        });

        // Fallback: Check if user has explicit permission matching the module
        const hasPermission = effectiveUser?.all_permissions && Object.keys(effectiveUser.all_permissions).some(p => 
            p.includes('certificate') || p.includes('ngo') || p.includes('manage') || p.includes('view')
        );

        if (!hasAccess && !hasPermission && navList.length > 0) {
            console.warn(`Access Denied: Module path '${requiredPath}' is not enabled for this organization.`);
            return <Navigate to="/forbidden" replace />;
        }
    }

    return children;
}
